import React, { useState, useMemo } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths
} from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiChevronLeft, FiChevronRight } = FiIcons;
import SafeIcon from '../common/SafeIcon';

const ScheduleCalendar = ({ games }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth));
    const end = endOfWeek(endOfMonth(currentMonth));
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const getGameForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    // Use officialDate to prevent evening games from spilling into the next day on UTC
    return games.find(g => g.officialDate === dayStr || (g.gameDate && g.gameDate.startsWith(dayStr)));
  };

  return (
    <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.05)] overflow-hidden">
      <div className="p-3 sm:p-6 border-b-2 border-[#658d71] flex justify-between items-center gap-3">
        <h3 className="text-base sm:text-xl font-bold tracking-widest text-[#d8ceb3] uppercase">
          Schedule <span className="text-[#a9c9b1] text-xs sm:text-sm font-medium">[{format(currentMonth, 'MMM yyyy')}]</span>
        </h3>
        <div className="flex items-center gap-2 bg-[rgba(0,0,0,0.2)] p-1 rounded-sm border border-[#658d71]">
          <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-1.5 hover:bg-[#658d71] text-[#d8ceb3] transition-colors rounded">
            <SafeIcon icon={FiChevronLeft} size={18} />
          </button>
          <div className="w-px h-5 bg-[#658d71]" />
          <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-1.5 hover:bg-[#658d71] text-[#d8ceb3] transition-colors rounded">
            <SafeIcon icon={FiChevronRight} size={18} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
          <div key={i} className="py-2 text-center text-[10px] font-bold uppercase tracking-widest text-[#a9c9b1]">
            <span className="hidden sm:inline">{'Sun Mon Tue Wed Thu Fri Sat'.split(' ')[i]}</span>
            <span className="sm:hidden">{day}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        <AnimatePresence mode="wait">
          {calendarDays.map((day, i) => {
            const game = getGameForDay(day);
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isToday = isSameDay(day, new Date());
            const isHome = game?.teams.home.team.id === 111;
            const isFinal = game?.status.abstractGameState === 'Final';
            const isLive = game?.status.abstractGameState === 'Live';

            return (
              <motion.div
                key={day.toString()}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, delay: i * 0.005 }}
                className={`min-h-[60px] sm:min-h-[100px] border-r border-b border-[#658d71] p-1 relative flex flex-col ${
                  !isCurrentMonth ? 'opacity-20' : ''
                } ${isToday ? 'bg-[rgba(216,206,179,0.05)]' : ''} ${game ? 'cursor-pointer group' : ''}`}
              >
                <span className={`text-[10px] sm:text-xs font-bold tabular-nums mb-0.5 ${
                  isToday ? 'text-[#c4333e]' : isCurrentMonth ? 'text-[#a9c9b1]' : 'text-gray-500'
                }`}>
                  {format(day, 'd')}
                </span>

                {game && (
                  <div className={`flex-1 flex flex-col justify-center items-center text-center rounded p-0.5 sm:p-1 ${
                    isHome ? 'border-l-2 border-[#d8ceb3] bg-[rgba(216,206,179,0.05)]' : 'bg-[rgba(0,0,0,0.2)]'
                  }`}>
                    <span className="text-[8px] sm:text-[10px] font-bold text-white uppercase leading-none mb-0.5">
                      {isHome ? 'vs' : '@'} {isHome ? game.teams.away.team.abbreviation : game.teams.home.team.abbreviation}
                    </span>
                    {isFinal ? (
                      <div className="flex flex-col items-center">
                        <span className="text-[8px] sm:text-[10px] font-black tracking-tighter text-[#a9c9b1]">
                          {game.teams.away.score}-{game.teams.home.score}
                        </span>
                        <span className="hidden sm:block text-[8px] uppercase font-bold text-[#a9c9b1]">Final</span>
                      </div>
                    ) : (
                      <span className={`text-[8px] sm:text-[10px] font-medium text-[#d8ceb3] ${isLive ? 'animate-pulse text-[#c4333e]' : ''}`}>
                        {isLive ? 'LIVE' : format(new Date(game.gameDate), 'h:mm')}
                      </span>
                    )}
                  </div>
                )}

                {game && (
                  <div className="absolute inset-0 bg-[#386646] opacity-0 group-hover:opacity-100 transition-opacity z-10 flex flex-col items-center justify-center p-1 text-center">
                    <span className="text-[8px] text-[#a9c9b1] uppercase tracking-widest mb-0.5">{format(new Date(game.gameDate), 'EEE')}</span>
                    <span className="text-[9px] text-white font-bold leading-tight">{game.teams.away.team.abbreviation} @ {game.teams.home.team.abbreviation}</span>
                    <span className="text-[8px] text-[#d8ceb3] font-bold mt-1">{game.venue?.name}</span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      <div className="p-3 bg-[rgba(0,0,0,0.1)] flex flex-wrap gap-4 justify-center text-[9px] sm:text-[10px] uppercase tracking-widest font-bold border-t border-[#658d71]">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 border-l-2 border-[#d8ceb3] bg-[rgba(216,206,179,0.1)]" />
          <span className="text-[#d8ceb3]">Home</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-[rgba(0,0,0,0.2)]" />
          <span className="text-[#a9c9b1]">Away</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-[#c4333e] animate-pulse" />
          <span className="text-white">Live</span>
        </div>
      </div>
    </div>
  );
};

export default ScheduleCalendar;